import type { ContactsResultBlock } from "@containers/Hermes/types";

type ContactsResultsProps = {
  block: ContactsResultBlock;
};

type ResultRow = {
  key: string;
  companyName: string;
  name: string;
  title: string;
  location: string;
  email: string;
  phone: string;
  linkedin: string;
};

// Same table chrome/column composition as the "Employee Level Contacts" tab
// on a company profile (SearchContacts/index.jsx) — avatar-initial + name +
// inline LinkedIn icon, job title, location, contact details as pills. No
// reveal-button/credit-gating here since hermes/contacts/fetch already
// returns unlocked contacts.
const thCls = "tw-h-10 tw-px-4 tw-text-left tw-text-xs tw-font-medium tw-text-gray-900 tw-bg-white tw-border-b tw-border-gray-200";
const tdCls = "tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-normal tw-text-gray-700 tw-border-b tw-border-gray-100";

export default function ContactsResults({ block }: ContactsResultsProps) {
  const rows: ResultRow[] = (block.results ?? []).flatMap((company, companyIdx) =>
    (company.contacts ?? []).map((contact, contactIdx) => ({
      key: `${company.company_id ?? companyIdx}-${contactIdx}`,
      companyName: company.company_name || "",
      name: contact.full_name || [contact.first_name, contact.last_name].filter(Boolean).join(" "),
      title: contact.title || "",
      location: contact.location || "",
      email: contact.email || "",
      phone: contact.phone || "",
      linkedin: contact.linkedin || "",
    }))
  );

  if (block.deferred_to_email) {
    return <p className="tw-m-0 tw-mt-3 tw-text-xs tw-text-gray-500">This batch was too large to show here — we'll email the contacts to you instead.</p>;
  }

  if (rows.length === 0) {
    return <p className="tw-m-0 tw-mt-3 tw-text-xs tw-text-gray-500">No contacts were found for the selected companies.</p>;
  }

  return (
    <div className="tw-mt-3 tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden tw-overflow-x-auto">
      <table className="tw-table-fixed tw-border-collapse tw-w-[980px]">
        <colgroup>
          <col className="tw-w-[180px]" />
          <col className="tw-w-[200px]" />
          <col className="tw-w-[160px]" />
          <col className="tw-w-[140px]" />
          <col className="tw-w-[300px]" />
        </colgroup>
        <thead>
          <tr>
            <th className={thCls}>Company Name</th>
            <th className={thCls}>Name</th>
            <th className={thCls}>Job Title</th>
            <th className={thCls}>Location</th>
            <th className={thCls}>Contact Details</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className={tdCls}>
                <span className="tw-block tw-capitalize">{row.companyName || "-"}</span>
              </td>
              <td className={tdCls}>
                <NameCell name={row.name} linkedin={row.linkedin} />
              </td>
              <td className={tdCls}>
                <span className="tw-block tw-capitalize">{row.title || "-"}</span>
              </td>
              <td className={tdCls}>
                <span className="tw-block">{row.location || "-"}</span>
              </td>
              <td className={tdCls}>
                <ContactDetailsPills email={row.email} phone={row.phone} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function NameCell({ name, linkedin }: { name: string; linkedin: string }) {
  const initial = name.trim().charAt(0).toUpperCase();
  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
      <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
        <span className="tw-rounded-full tw-w-7 tw-h-7 tw-min-w-[28px] tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-medium tw-bg-[#ffd964]">{initial || "-"}</span>
        <span className="tw-text-xs tw-font-normal tw-capitalize tw-whitespace-nowrap tw-overflow-hidden tw-text-ellipsis">{name || "-"}</span>
      </div>
      {linkedin ? (
        <a className="tw-flex-shrink-0 tw-flex tw-items-center" href={linkedin} target="_blank" rel="noreferrer">
          <img src="https://res.cloudinary.com/drzta9shk/image/upload/v1784705761/intel/icons/linkedin_1_az6r2d.svg" alt="linkedin" className="tw-w-5 tw-h-5" />
        </a>
      ) : null}
    </div>
  );
}

function ContactDetailsPills({ email, phone }: { email: string; phone: string }) {
  if (!email && !phone) return <>-</>;
  const pillCls = "tw-flex tw-items-center tw-gap-1 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-shadow-autoSuggestPill tw-border tw-border-gray-200 tw-bg-white tw-text-xs tw-font-normal tw-text-gray-900";
  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
      {email ? (
        <span className={pillCls}>
          <EmailIcon /> {email}
        </span>
      ) : null}
      {phone ? (
        <span className={pillCls}>
          <PhoneIcon /> {phone}
        </span>
      ) : null}
    </div>
  );
}

function EmailIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="currentColor" className="tw-text-gray-900">
      <path d="M168-192q-29 0-50.5-21.5T96-264v-432q0-29 21.5-50.5T168-768h624q30 0 51 21.5t21 50.5v432q0 29-21 50.5T792-192H168Zm312-240 312-179v-85L480-517 168-696v85l312 179Z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="currentColor" className="tw-text-gray-900">
      <path d="M763-145q-121-9-229.5-59.5T339-341q-86-86-136-194.5T144-765q-2-21 12.5-36.5T192-817h136q17 0 29.5 10.5T374-780l24 107q2 13-1.5 25T385-628l-97 98q20 38 46 73t58 66q30 30 64 55.5t72 45.5l99-96q8-8 20-11.5t25-1.5l107 23q17 5 27 17.5t10 29.5v136q0 21-16 35.5T763-145Z" />
    </svg>
  );
}
